import React, { useEffect, useState } from "react";
import { api, fmtINR, todayISO, fmtDate } from "../lib/api";
import { Plus, Trash2, UserCircle, X, IndianRupee, Printer } from "lucide-react";
import { toast } from "sonner";
import { openBillWindow } from "../lib/bill";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [openingBal, setOpeningBal] = useState("");
  const [editOpening, setEditOpening] = useState("");
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [products, setProducts] = useState([]);
  const [rates, setRates] = useState({});
  const [multipliers, setMultipliers] = useState({});
  const [paperRates, setPaperRates] = useState({});
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payNotes, setPayNotes] = useState("");

  const load = async () => {
    const res = await api.get("/customers");
    setCustomers(res.data);
  };

  useEffect(() => {
    load();
    api.get("/products?active_only=true").then((r) => setProducts(r.data));
    api.get(`/paper-rates?date=${todayISO()}`).then((r) => {
      const m = {};
      r.data.forEach((p) => (m[p.category] = p.rate_per_kg));
      setPaperRates(m);
    });
  }, []);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/customers", { name, phone, address, opening_balance: parseFloat(openingBal) || 0 });
    toast.success("Customer added");
    setName("");
    setPhone("");
    setAddress("");
    setOpeningBal("");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer and all their saved rates?")) return;
    await api.delete(`/customers/${id}`);
    if (selected === id) setSelected(null);
    load();
  };

  const openLedger = async (c) => {
    setSelected(c.id);
    const [ledRes, rateRes, multRes] = await Promise.all([
      api.get(`/reports/customer-ledger?customer_id=${c.id}`),
      api.get(`/customer-rates?customer_id=${c.id}`),
      api.get(`/customer-multipliers?customer_id=${c.id}`),
    ]);
    setLedger(ledRes.data);
    setEditOpening(String(ledRes.data.opening_balance ?? 0));
    const map = {};
    rateRes.data.forEach((r) => (map[r.product_id] = r.price_per_kg));
    setRates(map);
    const mm = {};
    multRes.data.forEach((m) => (mm[m.product_id] = m.multiplier));
    setMultipliers(mm);
  };

  const saveRates = async () => {
    const items = Object.entries(rates)
      .filter(([_, v]) => v !== "" && v !== null && v !== undefined && parseFloat(v) > 0)
      .map(([product_id, price_per_kg]) => ({
        customer_id: selected,
        product_id,
        price_per_kg: parseFloat(price_per_kg),
      }));
    if (items.length === 0) {
      toast.error("Enter at least one rate");
      return;
    }
    await api.post("/customer-rates/bulk", items);
    toast.success("Saved customer rates");
  };

  const saveMultipliers = async () => {
    const items = Object.entries(multipliers)
      .filter(([_, v]) => v !== "" && v !== null && v !== undefined && parseFloat(v) > 0)
      .map(([product_id, multiplier]) => ({
        customer_id: selected,
        product_id,
        multiplier: parseFloat(multiplier),
      }));
    if (items.length === 0) {
      toast.error("Enter at least one multiplier");
      return;
    }
    await api.post("/customer-multipliers/bulk", items);
    toast.success("Saved customer multipliers");
  };

  const clearRate = async (productId) => {
    await api.delete(`/customer-rates?customer_id=${selected}&product_id=${productId}`);
    setRates((r) => { const x = { ...r }; delete x[productId]; return x; });
  };

  const clearMultiplier = async (productId) => {
    await api.delete(`/customer-multipliers?customer_id=${selected}&product_id=${productId}`);
    setMultipliers((m) => { const x = { ...m }; delete x[productId]; return x; });
  };

  const saveOpening = async () => {
    const c = ledger.customer;
    await api.put(`/customers/${c.id}`, {
      name: c.name,
      phone: c.phone || "",
      address: c.address || "",
      notes: c.notes || "",
      opening_balance: parseFloat(editOpening) || 0,
    });
    toast.success("Opening balance saved");
    const ledRes = await api.get(`/reports/customer-ledger?customer_id=${c.id}`);
    setLedger(ledRes.data);
  };

  const recordPayment = async () => {
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    await api.post("/customer-payments", {
      customer_id: selected,
      date: todayISO(),
      amount,
      mode: payMode,
      notes: payNotes,
    });
    toast.success(`Recorded ${fmtINR(amount)} payment`);
    setPayAmount("");
    setPayNotes("");
    const ledRes = await api.get(`/reports/customer-ledger?customer_id=${selected}`);
    setLedger(ledRes.data);
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    await api.delete(`/customer-payments/${id}`);
    const ledRes = await api.get(`/reports/customer-ledger?customer_id=${selected}`);
    setLedger(ledRes.data);
  };


  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="customers-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">B2B</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Customers
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Add New</div>
            <input
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="cust-name"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="cust-phone"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              placeholder="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="cust-address"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              type="number"
              placeholder="Opening balance ₹ (old dues, optional)"
              value={openingBal}
              onChange={(e) => setOpeningBal(e.target.value)}
              data-testid="cust-opening-balance"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <button
              onClick={add}
              data-testid="cust-add"
              className="w-full bg-sky-600 text-white px-4 py-2 text-sm uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add Customer
            </button>
          </div>

          <div className="border border-zinc-200 bg-white" data-testid="cust-list">
            {customers.length === 0 ? (
              <div className="p-5 text-sm text-zinc-500 text-center">No customers yet</div>
            ) : (
              customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => openLedger(c)}
                  data-testid={`cust-row-${c.id}`}
                  className={`w-full text-left border-b border-zinc-200 px-4 py-3 hover:bg-zinc-50 flex items-center justify-between ${
                    selected === c.id ? "bg-sky-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <UserCircle className="h-8 w-8 text-zinc-400" />
                    <div>
                      <div className="text-sm font-semibold">{c.name}</div>
                      {c.phone && <div className="text-xs text-zinc-500">{c.phone}</div>}
                    </div>
                  </div>
                  <Trash2
                    className="h-4 w-4 text-zinc-300 hover:text-sky-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      del(c.id);
                    }}
                  />
                </button>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!ledger ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
              Select a customer to view ledger and set saved rates
            </div>
          ) : (
            <div data-testid="cust-detail">
              <div className="border border-zinc-200 bg-white p-5 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Customer</div>
                    <div className="font-display text-2xl font-bold">{ledger.customer.name}</div>
                    {ledger.customer.phone && (
                      <div className="text-sm text-zinc-600">{ledger.customer.phone}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Outstanding</div>
                    <div
                      className={`font-display text-2xl font-bold font-mono-num ${
                        ledger.balance > 0 ? "text-sky-600" : "text-emerald-600"
                      }`}
                    >
                      {fmtINR(ledger.balance)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-zinc-200">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Opening Bal.</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs text-zinc-500">₹</span>
                      <input
                        type="number"
                        value={editOpening}
                        onChange={(e) => setEditOpening(e.target.value)}
                        data-testid="cust-edit-opening"
                        className="w-20 border border-zinc-300 px-2 py-1 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                      />
                      <button
                        onClick={saveOpening}
                        data-testid="cust-save-opening"
                        className="bg-zinc-900 text-white px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-zinc-800"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Billed</div>
                    <div className="font-mono-num font-semibold">{fmtINR(ledger.total_billed)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Paid</div>
                    <div className="font-mono-num font-semibold">{fmtINR(ledger.total_paid)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Orders</div>
                    <div className="font-mono-num font-semibold">{ledger.sales.length}</div>
                  </div>
                </div>
              </div>

              {/* Saved rates */}
              <div className="border border-zinc-200 bg-white p-5 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Saved Rates (highest priority — overrides everything)
                  </div>
                  <button
                    onClick={saveRates}
                    data-testid="cust-save-rates"
                    className="bg-zinc-900 text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-zinc-800"
                  >
                    Save
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {products.map((p) => (
                    <div key={p.id} className="flex items-center justify-between border border-zinc-200 px-3 py-2">
                      <span className="text-sm">{p.name}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-zinc-500">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          value={rates[p.id] ?? ""}
                          onChange={(e) => setRates((r) => ({ ...r, [p.id]: e.target.value }))}
                          data-testid={`cust-rate-${p.id}`}
                          className="w-20 border border-zinc-300 px-2 py-1 text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
                        />
                        {rates[p.id] != null && rates[p.id] !== "" && (
                          <button
                            onClick={() => clearRate(p.id)}
                            className="text-zinc-300 hover:text-rose-600 ml-1"
                            data-testid={`cust-rate-clear-${p.id}`}
                            title="Clear saved rate"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Paper-rate multipliers */}
              <div className="border border-zinc-200 bg-white p-5 mb-4" data-testid="cust-multipliers-section">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Paper-Rate Multipliers (price = paper rate × multiplier)
                  </div>
                  <button
                    onClick={saveMultipliers}
                    data-testid="cust-save-multipliers"
                    className="bg-zinc-900 text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-zinc-800"
                  >
                    Save
                  </button>
                </div>
                <div className="text-[10px] text-zinc-400 mb-3">
                  Applied to chicken products. Today&apos;s chicken paper:
                  {" "}
                  {paperRates.chicken
                    ? <b className="text-zinc-600">₹{Math.round(paperRates.chicken)}/kg</b>
                    : <span className="text-rose-500">not set — go to Paper Rate page first</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {products.filter((p) => p.category === "chicken").map((p) => {
                    const m = multipliers[p.id];
                    const pr = paperRates[p.category];
                    const computed = pr && m ? Math.round(pr * parseFloat(m)) : null;
                    return (
                      <div key={p.id} className="flex items-center justify-between border border-zinc-200 px-3 py-2">
                        <div className="text-sm flex-1 min-w-0 truncate">
                          {p.name}
                          {computed != null && (
                            <div className="text-[10px] text-emerald-700 font-mono-num">
                              → ₹{computed}/kg
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0"
                            value={multipliers[p.id] ?? ""}
                            onChange={(e) => setMultipliers((r) => ({ ...r, [p.id]: e.target.value }))}
                            data-testid={`cust-mult-${p.id}`}
                            className="w-16 border border-zinc-300 px-2 py-1 text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
                          />
                          <span className="text-xs text-zinc-500">×</span>
                          {multipliers[p.id] != null && multipliers[p.id] !== "" && (
                            <button
                              onClick={() => clearMultiplier(p.id)}
                              className="text-zinc-300 hover:text-rose-600 ml-1"
                              data-testid={`cust-mult-clear-${p.id}`}
                              title="Clear multiplier"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Payment recording */}
              <div className="border border-zinc-200 bg-white p-5 mb-4" data-testid="cust-payment-section">
                <div className="flex items-center gap-2 mb-3">
                  <IndianRupee className="h-4 w-4 text-emerald-600" />
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Record Payment
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount ₹"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    data-testid="cust-pay-amount"
                    className="md:col-span-3 border border-zinc-300 px-3 py-2 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                  <select
                    value={payMode}
                    onChange={(e) => setPayMode(e.target.value)}
                    data-testid="cust-pay-mode"
                    className="md:col-span-3 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </select>
                  <input
                    placeholder="Notes (optional)"
                    value={payNotes}
                    onChange={(e) => setPayNotes(e.target.value)}
                    data-testid="cust-pay-notes"
                    className="md:col-span-4 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                  <button
                    onClick={recordPayment}
                    data-testid="cust-pay-save"
                    className="md:col-span-2 bg-emerald-600 text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-emerald-700"
                  >
                    Record
                  </button>
                </div>

                {(ledger.payments || []).length > 0 && (
                  <div className="border-t border-zinc-200 mt-3 pt-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                      Payment History
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {(ledger.payments || []).map((p) => (
                          <tr key={p.id} className="border-b border-zinc-100" data-testid={`payment-${p.id}`}>
                            <td className="py-1.5">{fmtDate(p.date)}</td>
                            <td className="py-1.5 uppercase text-zinc-500">{p.mode}</td>
                            <td className="py-1.5">{p.notes || "-"}</td>
                            <td className="py-1.5 font-mono-num text-right font-semibold text-emerald-700">
                              {fmtINR(p.amount)}
                            </td>
                            <td className="py-1.5 text-right">
                              <button
                                onClick={() => deletePayment(p.id)}
                                className="text-zinc-300 hover:text-sky-600"
                                data-testid={`payment-delete-${p.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Sales history */}
              <div className="border border-zinc-200 bg-white overflow-x-auto">
                <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Order History
                </div>
                {ledger.sales.length === 0 ? (
                  <div className="p-6 text-center text-sm text-zinc-500">No orders yet</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Items</th>
                        <th className="px-4 py-2 text-right">Amount</th>
                        <th className="px-4 py-2 text-right">Paid</th>
                        <th className="px-4 py-2 text-right">Print</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.sales.map((s) => (
                        <tr key={s.id} className="border-b border-zinc-200" data-testid={`order-${s.id}`}>
                          <td className="px-4 py-2 text-xs">{fmtDate(s.date)}</td>
                          <td className="px-4 py-2 text-xs">
                            {s.items.map((i) => i.product_name).join(", ")}
                          </td>
                          <td className="px-4 py-2 text-xs font-mono-num text-right font-semibold">
                            {fmtINR(s.total_amount)}
                          </td>
                          <td className="px-4 py-2 text-xs font-mono-num text-right">
                            {fmtINR(s.paid_amount)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => openBillWindow(s)}
                              className="text-zinc-500 hover:text-sky-600"
                              data-testid={`order-print-${s.id}`}
                            >
                              <Printer className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
